/**
 * Placeholder for embedded content (iframe / embed / object) in the feedback
 * screenshot. Pure helpers — the DOM work happens in `capture_screenshot.ts`,
 * `npm test` (node --test) covers this module.
 *
 * Why: modern-screenshot renders the page through an SVG `<foreignObject>`.
 * It cannot read a cross-origin iframe (the offer PDF preview served by the
 * PDF renderer, signed-URL PDF previews) or a `sandbox=""` srcDoc iframe (the
 * mail body in the Bestelleingang), so it clones an empty frame — and that
 * area came out solid black in every offer-page feedback screenshot. A
 * labelled placeholder of the same box makes clear what was there. Swapping
 * the element also drops the iframe `src`, which for the offer preview
 * carries the user's Bearer token.
 */

/** Elements that are replaced by the placeholder in the screenshot clone. */
export const EMBEDDED_CONTENT_SELECTOR = "iframe, embed, object";

/** Text shown inside the placeholder. */
export const EMBEDDED_CONTENT_PLACEHOLDER_TEXT =
  "Eingebettete Vorschau – im Screenshot nicht darstellbar";

/**
 * Style overrides for the placeholder `<div>`. Everything else (size,
 * position, margins, flex/grid item props) is copied from the clone's inline
 * style, which modern-screenshot fills from the computed style of the
 * original element — so the placeholder occupies exactly the original box.
 *
 * @param display the clone's inline `display` ("" = the element default,
 *   which for iframe/embed/object is `inline`).
 */
export function embeddedPlaceholderStyle(display: string): Record<string, string> {
  const d = display.trim();
  let placeholderDisplay: string;
  if (d === "none") placeholderDisplay = "none";
  else if (d === "" || d.startsWith("inline")) placeholderDisplay = "inline-flex";
  else placeholderDisplay = "flex";

  return {
    display: placeholderDisplay,
    "align-items": "center",
    "justify-content": "center",
    "text-align": "center",
    overflow: "hidden",
    // A replaced element sits on the baseline with its bottom edge — an
    // inline-flex box with text would otherwise shift to the text baseline.
    "vertical-align": "bottom",
    "background-color": "#ececec",
    "background-image": "none",
    color: "#595959",
    // modern-screenshot inlines `-webkit-text-fill-color` on every clone (it
    // is excluded from its default-style diff), so the copied page text
    // colour would win over `color` — set both.
    "-webkit-text-fill-color": "#595959",
    "font-size": "14px",
    "font-style": "italic",
    "line-height": "1.4",
    // Outline instead of border: does not change the box size.
    outline: "1px dashed #bfbfbf",
    "outline-offset": "-1px",
  };
}
